import React, { useState, useMemo, useEffect } from "react";
import { createPortal } from "react-dom";
import CustomToolTip from "../../../../../CommonComponents/CustomToolTip";
import ConfirmationDialog from "../../../../../CommonComponents/confirmation_dialog/ConfirmationDialog";
import { AngularLoadingSpinner } from "../../Files/Svgs";
import { filterVisibleExecutedTests } from "../../../../../validate/helpers/validateWsHelpers";
import "./QueuePanel.scss";

export type QueueTestStatus =
  | "running"
  | "passed"
  | "failed"
  | "aborted"
  | "cancelled";

export type QueueTestType = "Test Plan" | "Test Case";

export interface QueueTest {
  id: string | number;
  name: string;
  duration: string;
  remainingDuration?: string;
  completed: number;
  total: number;
  passed: number;
  failed: number;
  testCasesPassed?: number;
  testCasesFailed?: number;
  totalTestCases?: number;
  percentComplete?: number;
  remainingPercent?: number;
  status?: QueueTestStatus;
  rawStatus?: string;
  statusLabel?: string;
  /** Highlights an executed card like the Figma selected state. */
  selected?: boolean;
  /** Identifies whether this is a Test Plan or a Test Case */
  testType?: QueueTestType;
  /** Current test case executing inside a test plan */
  currentTestCaseName?: string;
  testPlanId?: string;
  testCaseId?: string;
  testPlanName?: string;
  testCaseName?: string;
  testPlanResultId?: string;
  testCaseResultId?: string;
}

export interface QueuePanelProps {
  /**
   * Current active test.
   * Normally populated by the initial GET API and updated by WebSocket events.
   */
  activeTest?: QueueTest | null;

  /**
   * Executed test list.
   * Normally populated by the GET API.
   */
  executedTests?: QueueTest[];

  /**
   * Optional total count returned separately by the API.
   * Defaults to executedTests.length.
   */
  executedTestsCount?: number;

  /**
   * External abort loading state from parent hook.
   */
  isAborting?: boolean;

  /**
   * Parent handles the actual Abort API request.
   */
  onAbort?: (test: QueueTest) => void | Promise<any>;

  /**
   * Called when the Queue drawer is closed.
   */
  onClose?: () => void;

  className?: string;

  /** Figma default width: 296px. */
  width?: number | string;

  /** Figma default height: 692px. */
  height?: number | string;

  /** Controls drawer slide-in visibility */
  isOpen?: boolean;
}

const ASSETS = {
  close:
    "data:image/svg+xml,%3Csvg xmlns='http://www.w3.org/2000/svg' width='16' height='16' viewBox='0 0 16 16' fill='none'%3E%3Cpath d='M4 12L12 4M4 4l8 8' stroke='%23F0F0F0' stroke-width='1.5' stroke-linecap='round'/%3E%3C/svg%3E",
  taskStatus:
    "data:image/svg+xml,%3Csvg xmlns='http://www.w3.org/2000/svg' width='14' height='10' viewBox='0 0 14 10' fill='none'%3E%3Cpath d='M1.5 5L5 8.5L12.5 1.5' stroke='%23CCF54E' stroke-width='2' stroke-linecap='round' stroke-linejoin='round'/%3E%3C/svg%3E",
  alert:
    "data:image/svg+xml,%3Csvg xmlns='http://www.w3.org/2000/svg' width='15' height='13' viewBox='2.5 3 11 10' fill='none'%3E%3Cpath d='M12.2767 10.6399L8.75948 4.48482C8.68279 4.3495 8.57158 4.23694 8.43719 4.15863C8.30279 4.08032 8.15004 4.03906 7.99449 4.03906C7.83895 4.03906 7.68619 4.08032 7.5518 4.15863C7.41741 4.23694 7.30619 4.3495 7.2295 4.48482L3.71231 10.6399C3.63479 10.7742 3.59414 10.9265 3.59448 11.0815C3.59483 11.2366 3.63615 11.3887 3.71426 11.5227C3.79237 11.6566 3.9045 11.7674 4.03927 11.844C4.17405 11.9206 4.32668 11.9603 4.48169 11.9589H11.5161C11.6704 11.9587 11.8219 11.918 11.9554 11.8407C12.089 11.7635 12.1998 11.6525 12.2769 11.5188C12.354 11.3852 12.3945 11.2336 12.3945 11.0793C12.3944 10.9251 12.3538 10.7735 12.2767 10.6399Z' stroke='%23FF1E27' stroke-width='1.2' stroke-linecap='round' stroke-linejoin='round'/%3E%3Cpath d='M7.99841 6.67969V8.43829' stroke='%23FF1E27' stroke-width='1.2' stroke-linecap='round' stroke-linejoin='round'/%3E%3Cpath d='M7.99841 10.1953H8.00177' stroke='%23FF1E27' stroke-width='1.2' stroke-linecap='round' stroke-linejoin='round'/%3E%3C/svg%3E",
};

const DEFAULT_ACTIVE_TEST: QueueTest = {
  id: "active-test",
  name: "ADCS Init Check",
  duration: "100 min",
  completed: 12,
  total: 120,
  passed: 10,
  failed: 2,
  status: "running",
  testType: "Test Plan",
};

const DEFAULT_EXECUTED_TESTS: QueueTest[] = [
  {
    id: "exec-1",
    name: "EPS Health Check",
    duration: "45 min",
    completed: 50,
    total: 50,
    passed: 50,
    failed: 0,
    status: "passed",
    testType: "Test Plan",
  },
  {
    id: "exec-2",
    name: "Thermal Loop Diagnostic",
    duration: "60 min",
    completed: 40,
    total: 60,
    passed: 32,
    failed: 8,
    status: "failed",
    testType: "Test Plan",
  },
  {
    id: "exec-3",
    name: "Payload Memory Sweep",
    duration: "15 min",
    completed: 5,
    total: 30,
    passed: 5,
    failed: 0,
    status: "aborted",
    testType: "Test Case",
  },
];

export const getTestTypeLabel = (test: QueueTest): QueueTestType => {
  if (test.testType) {
    return test.testType;
  }

  const lowerName = String(test.name || "").toLowerCase();
  const lowerId = String(test.id || "").toLowerCase();

  if (
    lowerName.includes("testplan") ||
    lowerName.includes("test_plan") ||
    lowerName.includes("test plan") ||
    lowerId.startsWith("tp") ||
    lowerId.includes("plan")
  ) {
    return "Test Plan";
  }

  if (
    lowerName.includes("testcase") ||
    lowerName.includes("test_case") ||
    lowerName.includes("test case") ||
    lowerId.startsWith("tc") ||
    lowerId.includes("case")
  ) {
    return "Test Case";
  }

  if (test.total > 1) {
    return "Test Plan";
  }

  return "Test Case";
};

const getProgress = (test: QueueTest): number => {
  if (test.percentComplete !== undefined && !isNaN(test.percentComplete)) {
    return Math.min(100, Math.max(0, test.percentComplete));
  }
  if (test.total <= 0) return 0;

  return Math.min(
    100,
    Math.max(0, (test.completed / test.total) * 100),
  );
};

const getRemainingPercentage = (test: QueueTest): number => {
  if (test.remainingPercent !== undefined && !isNaN(test.remainingPercent)) {
    return Math.min(100, Math.max(0, test.remainingPercent));
  }
  const progress = getProgress(test);
  return Math.max(0, Math.round(100 - progress));
};

const getRemainingTimeText = (test: QueueTest, active: boolean): string => {
  if (
    !active ||
    test.status === "passed" ||
    test.status === "failed" ||
    test.status === "aborted" ||
    test.status === "cancelled"
  ) {
    return test.duration || "";
  }

  const totalDuration = test.duration && test.duration !== "0 min" ? test.duration : "";

  if (test.remainingDuration) {
    if (test.remainingDuration.includes("(") && test.remainingDuration.includes(")")) {
      return test.remainingDuration;
    }

    const baseRem = test.remainingDuration.endsWith("left")
      ? test.remainingDuration
      : `${test.remainingDuration} left`;

    if (totalDuration) {
      return `${baseRem} (${totalDuration})`;
    }
    return baseRem;
  }

  const progress = getProgress(test);
  const match = totalDuration.match(/(\d+)\s*(\w*)/);
  if (!match) return totalDuration;

  const totalVal = parseFloat(match[1]);
  const unit = match[2] || "min";
  if (progress >= 100) return `0 ${unit} left (${totalDuration})`;

  const remainingVal = Math.max(0, Math.round(totalVal * (1 - progress / 100)));
  return `${remainingVal} ${unit} left (${totalDuration})`;
};

const getTooltipText = (test: QueueTest, active: boolean): string => {
  if (
    !active ||
    test.status === "passed" ||
    test.status === "failed" ||
    test.status === "aborted" ||
    test.status === "cancelled"
  ) {
    return test.duration ? `${test.duration} total` : "";
  }

  const totalDuration = test.duration && test.duration !== "0 min" ? test.duration : "";

  if (test.remainingDuration) {
    const parenIndex = test.remainingDuration.indexOf("(");
    const remPart =
      parenIndex !== -1
        ? test.remainingDuration.slice(0, parenIndex).trim()
        : test.remainingDuration.endsWith("left")
        ? test.remainingDuration
        : `${test.remainingDuration} left`;

    if (totalDuration) {
      return `${remPart} (${totalDuration} total)`;
    }
    return remPart;
  }

  const progress = getProgress(test);
  const match = totalDuration.match(/(\d+)\s*(\w*)/);
  if (!match) return totalDuration ? `${totalDuration} total` : "";

  const totalVal = parseFloat(match[1]);
  const unit = match[2] || "min";
  const remainingVal = Math.max(0, Math.round(totalVal * (1 - progress / 100)));

  return totalDuration
    ? `${remainingVal} ${unit} left (${totalDuration} total)`
    : `${remainingVal} ${unit} left`;
};

interface CardIconProps {
  active: boolean;
  status?: QueueTestStatus;
}

const CardIcon: React.FC<CardIconProps> = ({ active, status }) => {
  if (active) {
    return (
      <svg
        xmlns="http://www.w3.org/2000/svg"
        width="18"
        height="18"
        viewBox="2.5 2.5 13 13"
        fill="none"
        className="queue-test-card__icon"
        aria-hidden="true"
      >
        <path
          d="M9 14C11.7614 14 14 11.7614 14 9C14 6.23858 11.7614 4 9 4C6.23858 4 4 6.23858 4 9C4 11.7614 6.23858 14 9 14Z"
          stroke="#CCF54E"
          strokeWidth="1.2"
          strokeLinecap="round"
          strokeLinejoin="round"
        />
        <path
          d="M9 11C10.1046 11 11 10.1046 11 9C11 7.89543 10.1046 7 9 7C7.89543 7 7 7.89543 7 9C7 10.1046 7.89543 11 9 11Z"
          fill="#CCF54E"
        />
      </svg>
    );
  }

  if (status === "passed") {
    return (
      <svg
        xmlns="http://www.w3.org/2000/svg"
        width="18"
        height="18"
        viewBox="2 2 12 12"
        fill="none"
        className="queue-test-card__icon"
        aria-hidden="true"
      >
        <path
          d="M7.99919 12.722C10.6062 12.722 12.7196 10.6086 12.7196 8.00163C12.7196 5.39464 10.6062 3.28125 7.99919 3.28125C5.39219 3.28125 3.27881 5.39464 3.27881 8.00163C3.27881 10.6086 5.39219 12.722 7.99919 12.722Z"
          stroke="#CCF54E"
          style={{
            stroke: "color(display-p3 0.8000 0.9608 0.3059)",
            strokeOpacity: 1,
          }}
          strokeWidth="0.9"
          strokeLinecap="round"
          strokeLinejoin="round"
        />
        <path
          d="M5.45801 7.99935L7.15267 9.69401L10.542 6.30469"
          stroke="#CCF54E"
          style={{
            stroke: "color(display-p3 0.8000 0.9608 0.3059)",
            strokeOpacity: 1,
          }}
          strokeWidth="0.9"
          strokeLinecap="round"
          strokeLinejoin="round"
        />
      </svg>
    );
  }

  return (
    <svg
      xmlns="http://www.w3.org/2000/svg"
      width="18"
      height="18"
      viewBox="2.5 3 11 10"
      fill="none"
      className="queue-test-card__icon"
      aria-hidden="true"
    >
      <path
        d="M12.2767 10.6399L8.75948 4.48482C8.68279 4.3495 8.57158 4.23694 8.43719 4.15863C8.30279 4.08032 8.15004 4.03906 7.99449 4.03906C7.83895 4.03906 7.68619 4.08032 7.5518 4.15863C7.41741 4.23694 7.30619 4.3495 7.2295 4.48482L3.71231 10.6399C3.63479 10.7742 3.59414 10.9265 3.59448 11.0815C3.59483 11.2366 3.63615 11.3887 3.71426 11.5227C3.79237 11.6566 3.9045 11.7674 4.03927 11.844C4.17405 11.9206 4.32668 11.9603 4.48169 11.9589H11.5161C11.6704 11.9587 11.8219 11.918 11.9554 11.8407C12.089 11.7635 12.1998 11.6525 12.2769 11.5188C12.354 11.3852 12.3945 11.2336 12.3945 11.0793C12.3944 10.9251 12.3538 10.7735 12.2767 10.6399Z"
        stroke="#FFA940"
        strokeWidth="1.2"
        strokeLinecap="round"
        strokeLinejoin="round"
      />
      <path
        d="M7.99841 6.67969V8.43829"
        stroke="#FFA940"
        strokeWidth="1.2"
        strokeLinecap="round"
        strokeLinejoin="round"
      />
      <path
        d="M7.99841 10.1953H8.00177"
        stroke="#FFA940"
        strokeWidth="1.2"
        strokeLinecap="round"
        strokeLinejoin="round"
      />
    </svg>
  );
};

interface TestCardProps {
  test: QueueTest;
  active?: boolean;
  isAborting?: boolean;
  onAbort?: (test: QueueTest) => void | Promise<any>;
  onSelect?: (test: QueueTest) => void;
}

const TestCard: React.FC<TestCardProps> = ({
  test,
  active = false,
  isAborting: propIsAborting,
  onAbort,
  onSelect,
}) => {
  const isAborting = Boolean(propIsAborting);
  const progress = Math.round(getProgress(test));
  const remainingPercent = getRemainingPercentage(test);
  const remainingTimeText = getRemainingTimeText(test, active);
  const tooltipText = getTooltipText(test, active);
  const isAborted =
    test.status === "aborted" ||
    test.status === "cancelled" ||
    Boolean(
      test.rawStatus &&
        (test.rawStatus.toUpperCase().includes("ABORT") ||
          test.rawStatus.toUpperCase().includes("CANCEL")),
    );
  const isFailed = test.status === "failed";

  const handleAbortClick = (e: React.MouseEvent) => {
    e.stopPropagation();
    if (isAborting) return;
    onAbort?.(test);
  };

  return (
    <article
      onClick={() => onSelect?.(test)}
      className={[
        "queue-test-card",
        active
          ? "queue-test-card--active"
          : "queue-test-card--executed",
        test.selected ? "queue-test-card--selected" : "",
        isAborted ? "queue-test-card--aborted" : "",
      ]
        .filter(Boolean)
        .join(" ")}
    >
      <div className="queue-test-card__body">
        <div className="queue-test-card__top">
          <div className="queue-test-card__name">
            <CardIcon active={active} status={test.status} />

            <CustomToolTip
              title={
                test.currentTestCaseName && test.currentTestCaseName !== test.name
                  ? `${getTestTypeLabel(test)}: ${test.name} (Running: ${test.currentTestCaseName})`
                  : `${getTestTypeLabel(test)}: ${test.name}`
              }
              placement="top"
              className="queue-test-card__name-tooltip"
            >
              <span className="queue-test-card__title">{test.name}</span>
            </CustomToolTip>
          </div>

          <CustomToolTip title={tooltipText} placement="top">
            <span
              className={`queue-test-card__duration ${!active ? "queue-test-card__duration--executed" : ""}`}
            >
              {remainingTimeText}
            </span>
          </CustomToolTip>
        </div>

        <div className="queue-test-card__progress-container">
          <div
            className="queue-test-card__track"
            role="progressbar"
            aria-label={`${test.name}: ${test.completed} of ${test.total}`}
            aria-valuemin={0}
            aria-valuemax={Math.max(0, test.total)}
            aria-valuenow={Math.min(
              Math.max(0, test.completed),
              Math.max(0, test.total),
            )}
          >
            <div
              className={[
                "queue-test-card__fill",
                isFailed ? "queue-test-card__fill--error" : "",
                active ? "queue-test-card__fill--active" : "",
              ]
                .filter(Boolean)
                .join(" ")}
              style={{ width: `${progress}%` }}
            />
          </div>

          <div className="queue-test-card__progress-details">
            <span className="queue-test-card__percent">
              {progress}%
              {active && remainingPercent > 0 && remainingPercent < 100 ? (
                <span className="queue-test-card__rem-percent"> ({remainingPercent}% remaining)</span>
              ) : null}
            </span>

            <span className="queue-test-card__count">
              <strong>{test.completed}</strong>
              <span>/{test.total}</span>
            </span>
          </div>
        </div>

        <div className="queue-test-card__bottom">
          <div className="queue-test-card__stats">
            <span className="queue-status queue-status--success">
              <img src={ASSETS.taskStatus} alt="" aria-hidden="true" />
              <span>{test.passed}</span>
            </span>

            <span className="queue-status queue-status--error">
              <img src={ASSETS.alert} alt="" aria-hidden="true" />
              <span>{test.failed}</span>
            </span>
          </div>

          {active && !isAborted ? (
            isAborting ? (
              <div className="queue-test-card__abort-loading" title="Aborting...">
                <AngularLoadingSpinner />
              </div>
            ) : (
              <button
                type="button"
                className="queue-test-card__abort"
                onClick={handleAbortClick}
              >
                Abort
              </button>
            )
          ) : isAborted ? (
            <span className="queue-test-card__aborted-label">
              {test.statusLabel || test.rawStatus || "Manual Abort"}
            </span>
          ) : null}
        </div>
      </div>
    </article>
  );
};

export const QueuePanel: React.FC<QueuePanelProps> = ({
  activeTest = DEFAULT_ACTIVE_TEST,
  executedTests = DEFAULT_EXECUTED_TESTS,
  executedTestsCount,
  isAborting = false,
  onAbort,
  onClose,
  className = "",
  width = 296,
  height = "100%",
  isOpen = false,
}) => {
  const [selectedTestId, setSelectedTestId] = useState<string | number | null>(null);
  const [abortTargetTest, setAbortTargetTest] = useState<QueueTest | null>(null);

  const handleRequestAbort = (test: QueueTest) => {
    setAbortTargetTest(test);
  };

  const handleClose = () => {
    setAbortTargetTest(null);
    onClose?.();
  };

  useEffect(() => {
    if (!isOpen) {
      setAbortTargetTest(null);
    }
  }, [isOpen]);

  const visibleExecutedTests = useMemo(
    () => filterVisibleExecutedTests(executedTests, activeTest),
    [executedTests, activeTest],
  );

  const count =
    executedTestsCount !== undefined && executedTestsCount !== executedTests.length
      ? executedTestsCount
      : visibleExecutedTests.length;

  const style = {
    "--queue-panel-width":
      typeof width === "number" ? `${width}px` : width,
    "--queue-panel-height":
      typeof height === "number" ? `${height}px` : height,
  } as React.CSSProperties;

  return (
    <aside
      className={`queue-panel ${isOpen ? "queue-panel--open" : ""} ${className}`.trim()}
      style={style}
      data-node-id="41746:194807"
      aria-label="Queue"
    >
      <header className="queue-panel__header">
        <span className="queue-panel__title">Queue</span>

        <button
          type="button"
          className="queue-panel__close"
          onClick={handleClose}
          aria-label="Close queue"
        >
          <img src={ASSETS.close} alt="" aria-hidden="true" />
        </button>
      </header>

      <div className="queue-panel__content">
        <section className="queue-panel__section">
          <div className="queue-panel__section-label">
            Active Test
          </div>

          {activeTest ? (
            <TestCard
              test={activeTest}
              active
              isAborting={isAborting}
              onAbort={handleRequestAbort}
            />
          ) : (
            <div className="queue-panel__no-active">
              No active tests.
            </div>
          )}
        </section>

        <section className="queue-panel__section">
          <div className="queue-panel__executed-header">
            <span>Executed Tests</span>
            <span>{String(count).padStart(2, "0")}</span>
          </div>

          <div className="queue-panel__executed-list">
            {visibleExecutedTests.length === 0 ? (
              <p className="queue-panel__empty">
                No tests executed.
              </p>
            ) : (
              visibleExecutedTests.map((test) => (
                <TestCard
                  key={test.id}
                  test={{
                    ...test,
                    selected: test.selected ?? (selectedTestId === test.id),
                  }}
                  onSelect={() =>
                    setSelectedTestId((prev) => (prev === test.id ? null : test.id))
                  }
                />
              ))
            )}
          </div>
        </section>
      </div>

      {abortTargetTest &&
        createPortal(
          <ConfirmationDialog
            type="cancel"
            open={Boolean(abortTargetTest)}
            title={
              abortTargetTest.testType === "Test Plan"
                ? "Abort test plan?"
                : abortTargetTest.testType === "Test Case"
                ? "Abort test case?"
                : "Abort execution?"
            }
            description={
              abortTargetTest.name && !abortTargetTest.name.toLowerCase().startsWith("active-")
                ? `Aborting "${abortTargetTest.name}" will stop the execution immediately. This action cannot be undone.`
                : "Aborting will stop the current execution immediately. This action cannot be undone."
            }
            confirm_button_text="Abort"
            confirm_loading_text="Aborting..."
            cancel_button_text="Cancel"
            loading={isAborting}
            onCancel={() => {
              if (!isAborting) {
                setAbortTargetTest(null);
              }
            }}
            onConfirm={async () => {
              if (!abortTargetTest) return;
              try {
                await onAbort?.(abortTargetTest);
              } catch (err) {
                console.error("Failed to abort test:", err);
              } finally {
                setAbortTargetTest(null);
              }
            }}
            dataTestIdPrefix="queue-abort"
          />,
          document.body,
        )}
    </aside>
  );
};

export default QueuePanel;
